import type { FxQualityTier } from "./fxRuntime";

export type FxSegmentationTierTuning = {
  detectIntervalMs: number;
  sampleStride: number;
  frameStride: number;
  maskScale: number;
  maskEmaAlpha: number;
};

export type FxSegmentationTuningConfig = {
  runtimeQualityTier: FxQualityTier;
  tiers: Record<FxQualityTier, FxSegmentationTierTuning>;
};

const STORAGE_KEY = "vb:fx:segmentation-tuning:v1";

const DEFAULT_CONFIG: FxSegmentationTuningConfig = {
  runtimeQualityTier: "balanced",
  tiers: {
    draft: {
      detectIntervalMs: 220,
      sampleStride: 2,
      frameStride: 2,
      maskScale: 0.3,
      maskEmaAlpha: 0.3,
    },
    balanced: {
      detectIntervalMs: 140,
      sampleStride: 1,
      frameStride: 1,
      maskScale: 1,
      maskEmaAlpha: 0.45,
    },
    final: {
      detectIntervalMs: 90,
      sampleStride: 1,
      frameStride: 1,
      maskScale: 1,
      maskEmaAlpha: 0.62,
    },
  },
};

type Listener = () => void;

const listeners = new Set<Listener>();
let configCache: FxSegmentationTuningConfig = readInitialConfig();

export function subscribeFxSegmentationTuning(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getFxSegmentationTuningConfig(): FxSegmentationTuningConfig {
  return cloneConfig(configCache);
}

export function getFxSegmentationRuntimeQualityTier(): FxQualityTier {
  return configCache.runtimeQualityTier;
}

export function getFxSegmentationTierTuning(tier: FxQualityTier): FxSegmentationTierTuning {
  return { ...configCache.tiers[tier] };
}

export function setFxSegmentationRuntimeQualityTier(tier: FxQualityTier): void {
  if (configCache.runtimeQualityTier === tier) return;
  configCache = {
    ...configCache,
    runtimeQualityTier: tier,
  };
  persistAndEmit();
}

export function updateFxSegmentationTierTuning(
  tier: FxQualityTier,
  patch: Partial<FxSegmentationTierTuning>
): void {
  const current = configCache.tiers[tier];
  const next = sanitizeTierTuning(
    {
      ...current,
      ...patch,
    },
    current
  );
  if (
    current.detectIntervalMs === next.detectIntervalMs &&
    current.sampleStride === next.sampleStride &&
    current.frameStride === next.frameStride &&
    current.maskScale === next.maskScale &&
    current.maskEmaAlpha === next.maskEmaAlpha
  ) {
    return;
  }

  configCache = {
    ...configCache,
    tiers: {
      ...configCache.tiers,
      [tier]: next,
    },
  };
  persistAndEmit();
}

function emit(): void {
  for (const listener of listeners) listener();
}

function persistAndEmit(): void {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(configCache));
    } catch {}
  }
  emit();
}

function readInitialConfig(): FxSegmentationTuningConfig {
  if (typeof window === "undefined") return cloneConfig(DEFAULT_CONFIG);
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return cloneConfig(DEFAULT_CONFIG);
    const parsed = JSON.parse(raw) as Partial<FxSegmentationTuningConfig> | null;
    if (!parsed || typeof parsed !== "object") return cloneConfig(DEFAULT_CONFIG);
    return sanitizeConfig(parsed);
  } catch {
    return cloneConfig(DEFAULT_CONFIG);
  }
}

function sanitizeConfig(input: Partial<FxSegmentationTuningConfig>): FxSegmentationTuningConfig {
  const runtimeQualityTier = sanitizeTier(input.runtimeQualityTier);
  const tiersInput: Partial<Record<FxQualityTier, FxSegmentationTierTuning>> = input.tiers ?? {};
  return {
    runtimeQualityTier,
    tiers: {
      draft: sanitizeTierTuning(
        tiersInput.draft,
        DEFAULT_CONFIG.tiers.draft
      ),
      balanced: sanitizeTierTuning(
        tiersInput.balanced,
        DEFAULT_CONFIG.tiers.balanced
      ),
      final: sanitizeTierTuning(
        tiersInput.final,
        DEFAULT_CONFIG.tiers.final
      ),
    },
  };
}

function sanitizeTier(value: unknown): FxQualityTier {
  return value === "draft" || value === "balanced" || value === "final"
    ? value
    : DEFAULT_CONFIG.runtimeQualityTier;
}

function sanitizeTierTuning(
  value: unknown,
  fallback: FxSegmentationTierTuning
): FxSegmentationTierTuning {
  const input = (value ?? {}) as Partial<FxSegmentationTierTuning>;
  return {
    detectIntervalMs: clampInt(input.detectIntervalMs, 40, 400, fallback.detectIntervalMs),
    sampleStride: clampInt(input.sampleStride, 1, 4, fallback.sampleStride),
    frameStride: clampInt(input.frameStride, 1, 6, fallback.frameStride),
    maskScale: clampFloat(input.maskScale, 0.25, 1, fallback.maskScale),
    maskEmaAlpha: clampFloat(input.maskEmaAlpha, 0.05, 1, fallback.maskEmaAlpha),
  };
}

function cloneConfig(config: FxSegmentationTuningConfig): FxSegmentationTuningConfig {
  return {
    runtimeQualityTier: config.runtimeQualityTier,
    tiers: {
      draft: { ...config.tiers.draft },
      balanced: { ...config.tiers.balanced },
      final: { ...config.tiers.final },
    },
  };
}

function clampInt(value: unknown, min: number, max: number, fallback: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, Math.round(n)));
}

function clampFloat(value: unknown, min: number, max: number, fallback: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}
